/**
 * ナレーション分割位置の調整 (NarrationSplitEditorModal) の純粋関数。
 * 境界 (cuts) は行数 - 1 個の秒で、行 i の範囲は [cuts[i-1] ?? 0, cuts[i] ?? 総尺]。
 */

/** 1 行の最短尺 (BE の MIN_SEGMENT_SEC と揃える) */
export const MIN_SEGMENT_SEC = 0.1;
/** 保存値は 3 桁に丸めているので、これ以下の差は変更なしとみなす (BE の RANGE_EPSILON_SEC と揃える) */
export const RANGE_EPSILON_SEC = 0.002;

/** 各行の範囲から境界を作る (2 行目以降の開始秒) */
export function cutsFromRanges(ranges: ReadonlyArray<readonly [number, number]>): number[] {
  return ranges.slice(1).map(([start]) => start);
}

/** 境界から行の範囲を作る */
export function rangesFromCuts(cuts: readonly number[], total: number): Array<[number, number]> {
  const points = [0, ...cuts, total];
  return points.slice(0, -1).map((start, i) => [start, points[i + 1]]);
}

/** index 番目の境界を value へ動かす。隣の境界・両端から MIN_SEGMENT_SEC 以上離す */
export function moveCut(cuts: readonly number[], index: number, value: number, total: number): number[] {
  const lower = (index > 0 ? cuts[index - 1] : 0) + MIN_SEGMENT_SEC;
  const upper = (index < cuts.length - 1 ? cuts[index + 1] : total) - MIN_SEGMENT_SEC;
  const clamped = Math.min(Math.max(value, lower), Math.max(lower, upper));
  const next = [...cuts];
  next[index] = Math.round(clamped * 1000) / 1000;
  return next;
}

/** 元の範囲から変わった行の index (BE の changed_line_indexes と同じ判定・削除済みは除く) */
export function changedLineIndexes(
  original: ReadonlyArray<readonly [number, number]>,
  cuts: readonly number[],
  total: number,
  missing: readonly boolean[] = [],
): number[] {
  const next = rangesFromCuts(cuts, total);
  const out: number[] = [];
  next.forEach(([start, end], i) => {
    if (missing[i]) return;
    const [s0, e0] = original[i] ?? [NaN, NaN];
    if (!(Math.abs(s0 - start) <= RANGE_EPSILON_SEC && Math.abs(e0 - end) <= RANGE_EPSILON_SEC)) out.push(i);
  });
  return out;
}

/** 波形の高さの式は共通部品 (components/common/audioTrim/audioPeaks.ts) へ移した。既存の import 先を保つための再 export。 */
export { WAVEFORM_FLOOR_DB, waveHeightRatio } from '../../common/audioTrim/audioPeaks';

/** 秒を「m:ss.ss」で表示する */
export function formatSplitTime(sec: number): string {
  const safe = Math.max(0, sec);
  const m = Math.floor(safe / 60);
  const s = safe - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, '0')}`;
}
